import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { useAskSalahSubscription } from './useAskSalahSubscription';
import { ASK_SALAH_SUBSCRIPTION_ROUTE, SUBSCRIPTION_ROUTE } from 'src/routePaths';

/**
 * Hook to guard Ask Salah access and handle subscription expiration
 * 
 * This hook checks:
 * 1. User has SAL125 subscription
 * 2. User has Ask Salah subscription
 * 3. Ask Salah subscription is active and not expired
 * 
 * Returns:
 * - canAccess: Boolean indicating if user can access Ask Salah
 * - isExpired: Boolean indicating if subscription is expired
 * - reason: String explanation of why access is denied
 * 
 * Usage:
 * ```tsx
 * const { canAccess, isExpired } = useAskSalahAccessGuard();
 * 
 * if (!canAccess) {
 *   // User will be redirected automatically
 *   return null;
 * }
 * ```
 */
export const useAskSalahAccessGuard = (autoRedirect: boolean = false) => {
  const history = useHistory();
  const toast = useToast();
  const { formatMessage } = useIntl();
  
  const {
    hasSAL125Subscription,
    hasAskSalahAccess,
    subscription,
    isActive,
  } = useAskSalahSubscription();

  // Check if subscription exists but is expired
  // Expired = subscription exists AND endDate is in the past (regardless of status field)
  const isExpired = !!(
    subscription && 
    new Date(subscription.endDate) <= new Date()
  );
  
  const canAccess = hasSAL125Subscription && hasAskSalahAccess && isActive && !isExpired;
  
  console.log('[Access Guard] Check:', {
    hasSAL125Subscription,
    hasAskSalahAccess,
    isActive,
    isExpired,
    canAccess,
    endDate: subscription?.endDate,
    now: new Date().toISOString(),
  });

  useEffect(() => {
    if (!autoRedirect) return;

    // Handle different scenarios
    if (!hasSAL125Subscription) {
      toast({
        title: formatMessage({
          id: 'askSalah.error.noSAL125',
          defaultMessage: 'SAL125 Subscription Required',
        }),
        description: formatMessage({
          id: 'askSalah.error.noSAL125.description',
          defaultMessage: 'Please subscribe to SAL125 to access AskSalah.',
        }),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      history.push(SUBSCRIPTION_ROUTE);
      return;
    }

    if (isExpired) {
      toast({
        title: formatMessage({
          id: 'askSalah.error.expired',
          defaultMessage: 'Subscription Expired',
        }),
        description: formatMessage({
          id: 'askSalah.error.expired.description',
          defaultMessage: 'Your AskSalah subscription has expired. Please renew to continue.',
        }),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      history.push(ASK_SALAH_SUBSCRIPTION_ROUTE);
      return;
    }

    if (!hasAskSalahAccess) {
      toast({
        title: formatMessage({
          id: 'askSalah.error.noSubscription',
          defaultMessage: 'AskSalah Subscription Required',
        }),
        description: formatMessage({
          id: 'askSalah.error.noSubscription.description',
          defaultMessage: 'Please subscribe to AskSalah to access this feature.',
        }),
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      history.push(ASK_SALAH_SUBSCRIPTION_ROUTE);
      return;
    }
  }, [autoRedirect, hasSAL125Subscription, hasAskSalahAccess, isExpired, history, toast, formatMessage]);

  const getReason = () => {
    if (!hasSAL125Subscription) {
      return 'no_sal125_subscription';
    }
    if (isExpired) {
      return 'subscription_expired';
    }
    if (!hasAskSalahAccess) {
      return 'no_ask_salah_subscription';
    }
    return null;
  };

  return {
    canAccess,
    isExpired: !!isExpired,
    reason: getReason(),
    subscription,
  };
};

